import {
	FileClock,
	FileText,
	ShieldCheck,
	SlidersHorizontal,
	Users,
} from "lucide-react";
import { memoryMemberListSearch } from "./memory-browse";
import { OWNER_SECTIONS, type SectionIcon } from "./owner-sections";

export const PRIMARY_SECTIONS = ["activity", "context"] as const;
export type OrganizationSettingsSection = "general" | "policies" | "governance";

/** Preserve bookmarks and policy links made before Settings had sections. */
export function organizationSettingsSection(
	search: Record<string, unknown>,
	hash = "",
): OrganizationSettingsSection {
	if (
		search.section === "general" ||
		search.section === "policies" ||
		search.section === "governance"
	)
		return search.section;
	return search.connector ||
		search.connection ||
		search.agent ||
		hash === "policies"
		? "policies"
		: "general";
}

export function isSettingsDestination(
	owner: string,
	pathname: string,
): boolean {
	const first = pathname.startsWith(`/${owner}/`)
		? pathname.slice(owner.length + 2).split("/")[0]
		: "";
	return [
		"settings",
		"connectors",
		"agents",
		"automations",
		"$member",
		"%24member",
	].includes(first);
}

/**
 * Column width for a Settings destination page, shared by the page body and
 * the shell trail above it so the two edges cannot drift. `team` is a wide
 * member list and opts out of the cap.
 */
export function settingsDestinationWidth(key: string): string {
	if (key === "team") return "";
	if (key === "general" || key === "policies" || key === "governance") {
		return "max-w-4xl";
	}
	return "max-w-3xl";
}

interface SettingsDestination {
	key: string;
	label: string;
	icon: SectionIcon;
	to: string;
	params: Record<string, string>;
	search?: Record<string, unknown>;
	active: boolean;
	separator?: boolean;
}

export function settingsDestinations(
	owner: string,
	pathname: string,
	search: Record<string, unknown>,
	hash = "",
): SettingsDestination[] {
	const organizationPath = `/${owner}/settings/organization`;
	const inOrganization = pathname.replace(/\/$/, "") === organizationPath;
	const selected = organizationSettingsSection(search, hash);
	const organizationSection = (
		key: OrganizationSettingsSection,
		label: string,
		icon: SectionIcon,
	): SettingsDestination => ({
		key,
		label,
		icon,
		to: "/$owner/settings/organization",
		params: { owner },
		search: key === "general" ? {} : { section: key },
		active: inOrganization && selected === key,
	});
	return [
		organizationSection("general", "General", SlidersHorizontal),
		{
			key: "team",
			label: "Team",
			icon: Users,
			...memoryMemberListSearch(owner),
			active: [`/${owner}/$member`, `/${owner}/%24member`].some(
				(p) => pathname === p || pathname.startsWith(`${p}/`),
			),
		},
		...(["connectors", "agents", "automations"] as const).map((key, i) => ({
			...OWNER_SECTIONS[key],
			key,
			params: { owner },
			separator: i === 0,
			active:
				pathname === `/${owner}/${key}` ||
				pathname.startsWith(`/${owner}/${key}/`),
		})),
		{
			...organizationSection("policies", "Policies", ShieldCheck),
			separator: true,
		},
		organizationSection("governance", "Governance", FileText),
		{
			key: "audit",
			label: "Audit log",
			icon: FileClock,
			to: "/$owner/events",
			search: { semantic_types: "change", event_category: "config" },
			params: { owner },
			active:
				pathname === `/${owner}/events` && search.event_category === "config",
		},
	];
}
